/**
 * 혼합가스 폭발하한계(LEL)·폭발상한계(UEL) 계산.
 *
 * LEL 은 KOSHA GUIDE P-179-2022(ISO 10156 기반) 5.2.2 "ISO 10156 계산식
 * 적용방법"을 그대로 따른다 — 단순 Le Chatelier(불연성 가스를 전부 질소처럼
 * 취급)가 아니라, 불연성 성분마다 실제 억제력이 다른 것(질소등가계수 Kᵢ,
 * 예: CO2=1.5 는 질소보다 더 억제, Ar=0.55·He=0.9 는 덜 억제)을 보정한다.
 * 계산 순서(같은 지침 <부록 5> 예제로 교차검증 — H2 2%+CH4 6%+Ar 27%+
 * He 65% → LEL=48.04 vol% 재현 확인):
 *   1) 가연성 성분만 100%로 재정규화해 순수 Le Chatelier LEL(L'_M) 계산 (식 5-2)
 *      L'_M = 100 / Σ(aᵢ_정규화/LELᵢ)
 *   2) 불연성 성분의 질소등가계수 몰분율 가중평균 K (식 5-3)
 *      K = Σ(Kᵢ·Bᵢ) / Σ(Bᵢ)   (Bᵢ = 원래 몰% ,정규화 안 함)
 *   3) 폭발하한계 수정계수 Kf (식 5-6)
 *      Kf = [100 − L'_M − (1−K)·(ΣB/ΣA)·L'_M] / (100 − L'_M)
 *   4) 성분별 수정 LEL: LELᵢ' = Kf·LELᵢ, 최종 LEL 은 원래(재정규화 안 한)
 *      몰분율 Aᵢ 로 다시 Le Chatelier (식 5-1): LEL = 100 / Σ(Aᵢ/LELᵢ')
 * (질소만으로 희석하면 K=1 → Kf=1 이 되어 단순 Le Chatelier와 정확히 같다.)
 *
 * UEL 은 이 지침이 다루지 않는다 — 단순 Le Chatelier 식으로 구하되,
 * **가연성 성분끼리만 100%로 재정규화한 몰분율**을 쓴다(LEL 계산의 1단계
 * L'_M 과 같은 방식). 단순 Le Chatelier 식은 원래 "가연성 가스끼리의
 * 혼합비"에만 적용되는 식이라, 불연성 희석 효과를 보정 없이 원래(희석된)
 * 몰분율에 그대로 대입하면 안 된다 — 불연성 성분의 억제력을 반영할
 * 질소등가계수 보정(ISO 10156)이 LEL에는 있지만 UEL에는 없기 때문에,
 * 그 보정이 빠진 채로 희석된 몰분율을 쓰면 값이 실제보다 크게 부풀려진다.
 *
 * 불연성 성분 중 질소등가계수를 모르는 것(염소 등, gas-components.json
 * 참고)이 실제로 섞여 있으면 LEL 보정을 할 수 없으므로 null 을 반환한다
 * (임의로 계수를 추정하지 않는다).
 */
import { findGasComponent, type GasComponent } from "../data/gasComponents";

export interface ExplosiveLimitsResult {
  readonly molecularWeight: number;
  readonly lelVolPercent: number;
  /** UEL 데이터가 없는 가연성 성분이 섞여 있으면 null(LEL 은 구해져도 UEL 은 못 구할 수 있다). */
  readonly uelVolPercent: number | null;
  readonly totalMassFlowKgH: number;
}

/**
 * 성분별 질량유량(kg/h) 딕셔너리에서 혼합가스의 분자량·LEL·UEL 을 구한다.
 *
 * `customDefs` — 표준 성분 데이터베이스(`gasComponents.ts`)에 없는 key를
 * 임시로 풀이할 때 쓴다. 같은 key 가 `customDefs` 와 표준 DB 양쪽에 있으면
 * `customDefs` 가 우선한다.
 *
 * 알 수 없는 성분 key 가 있거나, 유량 합이 0 이거나, 가연성 성분이 하나도
 * 없어 LEL 을 구할 수 없거나, 질소등가계수를 모르는 불연성 성분이 섞여
 * 있으면 null.
 */
export function mixExplosiveLimits(
  components: Readonly<Record<string, number>>,
  customDefs?: Readonly<Record<string, GasComponent>>,
): ExplosiveLimitsResult | null {
  const parsed: Array<{ c: GasComponent; moles: number }> = [];
  let totalMass = 0;
  let totalMoles = 0;

  for (const [key, massFlowKgH] of Object.entries(components)) {
    if (!Number.isFinite(massFlowKgH) || massFlowKgH <= 0) continue;
    const c = customDefs?.[key] ?? findGasComponent(key);
    if (c === null || c === undefined) return null;
    const moles = massFlowKgH / c.molecularWeight;
    parsed.push({ c, moles });
    totalMass += massFlowKgH;
    totalMoles += moles;
  }
  if (totalMass <= 0 || totalMoles <= 0) return null;

  const flammables: Array<{ molPercent: number; lel: number; uel: number | null }> = [];
  const inerts: Array<{ molPercent: number; k: number | null }> = [];

  for (const { c, moles } of parsed) {
    const molPercent = (moles / totalMoles) * 100;
    if (c.lelVolPercent !== null) {
      flammables.push({ molPercent, lel: c.lelVolPercent, uel: c.uelVolPercent });
    } else {
      inerts.push({ molPercent, k: c.nitrogenEquivalent ?? null });
    }
  }

  if (flammables.length === 0) return null;
  if (inerts.some((b) => b.k === null && b.molPercent > 0)) return null;

  const sumA = flammables.reduce((s, f) => s + f.molPercent, 0);
  const sumB = inerts.reduce((s, b) => s + b.molPercent, 0);

  let normalizedReciprocalSum = 0;
  for (const f of flammables) {
    normalizedReciprocalSum += ((f.molPercent / sumA) * 100) / f.lel;
  }
  const flammableOnlyLel = 100 / normalizedReciprocalSum;

  const K = sumB > 0 ? inerts.reduce((s, b) => s + b.k! * b.molPercent, 0) / sumB : 1;

  const kf =
    (100 - flammableOnlyLel - (1 - K) * (sumB / sumA) * flammableOnlyLel) / (100 - flammableOnlyLel);

  let lelReciprocalSum = 0;
  for (const f of flammables) {
    lelReciprocalSum += f.molPercent / (f.lel * kf);
  }
  const lelVolPercent = 100 / lelReciprocalSum;

  const uelVolPercent = flammables.some((f) => f.uel === null)
    ? null
    : 100 / flammables.reduce((s, f) => s + ((f.molPercent / sumA) * 100) / f.uel!, 0);

  return {
    molecularWeight: totalMass / totalMoles,
    lelVolPercent,
    uelVolPercent,
    totalMassFlowKgH: totalMass,
  };
}
